import React, { PureComponent } from 'react'
import injectJss from 'react-jss'
import classNames from 'classnames'
import withProps from 'recompose/withProps'
import compose from 'recompose/compose'
import withStateHandlers from 'recompose/withStateHandlers'
import { TweenLite, Power2 } from 'gsap'
import PropTypes from 'prop-types'
import Indicators from './Indicators'
import Slide from './Slide'
import Clamp from 'aria-viewer/utils/clamp'
import Button from 'aria-viewer/tour/components/buttons/button'
import mapSelectorsToProps from 'aria-viewer/core/selectors/mapSelectorsToProps'
import { connect } from 'react-redux'
import { Icon } from 'aria-viewer/common/components/Icon'

const style = theme => ({
    container: {
        height: ({ isMobile }) => (!isMobile ? '100%' : null),
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        position: 'relative',
        alignSelf: ({ isMobile }) => (isMobile ? 'flex-start' : 'center'),
        boxSizing: 'border-box',
        '& *': {
            boxSizing: 'border-box',
        },
    },
    innerContent: {
        height: '100%',
    },
    carouselWrapper: {
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        boxShadow: '0 8px 20px 0 rgba(0, 0, 0, 0.5)',
    },
    carousel: {
        height: '100%',
        width: ({ mediaList = [] }) => `${mediaList.length * 100}%`,
        display: 'flex',
        justifyContent: ({ justifyCarouselContent }) =>
            justifyCarouselContent ? justifyCarouselContent : 'flex-start',
        alignItems: 'center',
    },
    arrow: {
        position: 'absolute',
        transform: 'translateX(-50%) translateY(-50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '50%',
        height: 56,
        width: 56,
        cursor: 'pointer',
    },
    leftArrow: {
        display: ({ hideLeftArrow, showArrows }) => (hideLeftArrow || !showArrows ? 'none' : 'flex'),
        top: '50%',
        left: ({ borderless }) => (borderless ? '44px' : '0%'),
    },
    rightArrow: {
        display: ({ hideRightArrow, showArrows }) => (hideRightArrow || !showArrows ? 'none' : 'flex'),
        top: '50%',
        left: ({ borderless }) => (borderless ? 'calc(100% - 44px)' : '100%'),
    },
    arrowIcon: {
        color: '#fff',
        fontSize: 36,
    },
    lowerContent: {
        width: ({ borderless }) => (borderless ? 'initial' : '100%'),
        display: ({ showLowerContent }) => (showLowerContent ? 'flex' : 'none'),
        justifyContent: ({ borderless }) => (borderless ? 'flex-start' : 'space-around'),
        alignItems: 'center',
        position: ({ borderless }) => (borderless ? 'absolute' : 'static'),
        bottom: 32,
        left: 32,
        right: 32,
    },
    landscapeLowerContent: {
        display: ({ showLowerContent }) => (showLowerContent ? 'flex' : 'none'),
        justifyContent: ({ borderless }) => (borderless ? 'flex-start' : 'space-around'),
        alignItems: 'center',
        position: 'absolute',
        bottom: 0,
        marginLeft: '-34px',
        width: '100vw',
        zIndex: 1,
    },
    desktopTextContent: {
        width: ({ isMobile }) => (isMobile ? '100%' : '90%'),
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'flex-start',
        padding: ({ isMobile }) => (isMobile ? '0px 24px 48px' : ''),
        marginTop: ({ borderless }) => (borderless ? 0 : 36),
        flexDirection: ({ borderless, isFullWidthTitleDescription, mediaList, currentIndex }) =>
            borderless || isFullWidthTitleDescription || !mediaList[currentIndex].description ? 'column' : 'row',
    },
    mobileShortTextContent: {
        width: ({ isMobile }) => (isMobile ? '100%' : '90%'),
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'flex-start',
        flexDirection: ({ borderless, isFullWidthTitleDescription, mediaList, currentIndex }) =>
            borderless || isFullWidthTitleDescription || !mediaList[currentIndex].description ? 'column' : 'row',
        '@media screen and (orientation: landscape)': {
            animation: 'slideDown 0.5s forwards',
            padding: '0px 35px 50px',
            position: 'fixed',
        },
    },
    '@keyframes slideDown': {
        '0%': {
            bottom: '100px',
        },
        '100%': {
            bottom: '0px',
        },
    },
    '@keyframes slideUp': {
        '0%': {
            bottom: '-100px',
        },
        '100%': {
            bottom: '0px',
        },
    },
    mobileLongTextContent: {
        width: ({ isMobile }) => (isMobile ? '100%' : '90%'),
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'flex-start',
        flexDirection: ({ borderless, isFullWidthTitleDescription, mediaList, currentIndex }) =>
            borderless || isFullWidthTitleDescription || !mediaList[currentIndex].description ? 'column' : 'row',
        '@media screen and (orientation: landscape)': {
            backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.7))',
            animation: 'slideUp 0.5s forwards',
            padding: '90px 35px 10px',
            position: 'fixed',
            minHeight: '100vh',
        },
        '@media screen and (orientation: portrait)': {
            marginTop: ({ borderless }) => (borderless ? 0 : 36),
            padding: ({ isMobile }) => (isMobile ? '0px 24px 48px' : ''),
        },
    },
    title: {
        fontSize: '36px',
        lineHeight: '46px',
        fontFamily: theme.typography.fontFamily,
        textShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
        color: theme.palette.common.white,
        width: '100%',
        maxWidth: ({ borderless, isFullWidthTitleDescription, mediaList, currentIndex }) =>
            borderless || isFullWidthTitleDescription || !mediaList[currentIndex].description ? 'auto' : '33%',
        '& > p > a': {
            color: theme.palette.common.white,
            textDecoration: 'underline',
            '&:hover': {
                textDecorationColor: 'blue',
            },
        },
        [theme.breakpoints.down('xs')]: {
            fontSize: '28px',
        },
    },
    desktopDescription: {
        fontSize: '15px',
        lineHeight: '22px',
        fontFamily: theme.typography.fontFamily,
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
        color: '#fff',
        maxWidth: ({ borderless, isFullWidthTitleDescription, mediaList, currentIndex }) =>
            borderless || isFullWidthTitleDescription || !mediaList[currentIndex].description ? 'auto' : '50%',
        marginTop: ({ borderless, isFullWidthTitleDescription, mediaList, currentIndex }) =>
            borderless || isFullWidthTitleDescription || !mediaList[currentIndex].description ? 16 : 0,
        '& > p > a': {
            color: 'white',
            textDecoration: 'underline',
            '&:hover': {
                textDecorationColor: 'blue',
            },
        },
        [theme.breakpoints.down('xs')]: {
            '& > p': {
                margin: '0',
            },
        },
    },
    mobileShortDescription: {
        fontSize: '15px',
        lineHeight: '22px',
        fontFamily: theme.typography.fontFamily,
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
        color: '#fff',
        maxWidth: ({ borderless, isFullWidthTitleDescription, mediaList, currentIndex }) =>
            borderless || isFullWidthTitleDescription || !mediaList[currentIndex].description ? 'auto' : '50%',
        marginTop: ({ borderless, isFullWidthTitleDescription, mediaList, currentIndex, isLandscape }) =>
            (borderless || isFullWidthTitleDescription || !mediaList[currentIndex].description) && !isLandscape
                ? 16
                : 0,
        '& > p > a': {
            color: 'white',
            textDecoration: 'underline',
            '&:hover': {
                textDecorationColor: 'blue',
            },
        },
        [theme.breakpoints.down('xs')]: {
            '& > p': {
                margin: '0',
            },
        },
    },
    mobileLongDescription: {
        fontSize: '15px',
        lineHeight: '22px',
        fontFamily: theme.typography.fontFamily,
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
        color: '#fff',
        paddingBottom: '2em',
        zIndex: 1,
        maxWidth: ({ borderless, isFullWidthTitleDescription, mediaList, currentIndex }) =>
            borderless || isFullWidthTitleDescription || !mediaList[currentIndex].description ? 'auto' : '50%',
        '& > p > a': {
            color: 'white',
            textDecoration: 'underline',
            '&:hover': {
                textDecorationColor: 'blue',
            },
        },
        [theme.breakpoints.down('xs')]: {
            '& > p': {
                margin: '0',
            },
        },
        '@media screen and (orientation: landscape)': {
            height: '180px',
            marginBottom: '30px',
            overflow: 'scroll',
            '& p': {
                marginTop: 0,
            },
        },
        '@media screen and (orientation: portrait)': {
            marginTop: '16px',
            paddingBottom: '2em',
        },
    },
    '@keyframes fadeIn': {
        '0%': {
            opacity: 0,
        },
        '100%': {
            opacity: 1,
        },
    },
    mobileLongDescriptionBackground: {
        backgroundColor: 'rgba(0,0,0,0.7)',
        animation: 'fadeIn 0.5s forwards',
        width: '100vw',
        height: '100vh',
        position: 'fixed',
        top: 0,
    },
    readMoreContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        position: 'absolute',
        bottom: '20px',
        zIndex: 1,
    },
    readMoreButton: {
        marginTop: 6,
    },
    expandIcons: {
        paddingTop: '8px',
        paddingLeft: '2px',
        fontSize: '30px',
        display: 'inline-block',
    },
    expandText: {
        width: '50px',
        display: 'inline-block',
    },
    mobileShortDescriptionBackground: {
        backgroundColor: 'rgba(0,0,0,0.7)',
        width: '100vw',
        height: '80px',
        position: 'fixed',
        left: 0,
        bottom: 0,
        zIndex: 0,
    },
})

const inlineStyle = {
    readMoreButton: {
        fontSize: 16,
        color: '#fff',
    },
}

const noop = () => null
let carousel

const props = withProps(
    ({
        currentIndex,
        maxIndex,
        showIndicators = true,
        showArrows = true,
        showLowerContent = true,
        render = noop,
        borderless = false,
    }) => {
        return {
            hideLeftArrow: currentIndex === 0,
            hideRightArrow: currentIndex === maxIndex || maxIndex === 0,
            showIndicators: showIndicators && maxIndex > 0,
            showArrows: showArrows && maxIndex > 0,
            showLowerContent: showLowerContent,
            render: render,
            borderless: borderless,
        }
    }
)

const state = withStateHandlers(
    ({ currentIndex = 0, onClick = noop, mediaList = [], onIndexChange = noop, onPrev = noop, onNext = noop }) => ({
        currentIndex,
        maxIndex: mediaList.length - 1,
        onClick,
        mediaList,
        onIndexChange,
        onPrev,
        onNext,
    }),
    {
        handleLeftArrClick: ({ currentIndex, maxIndex, onIndexChange, onPrev }) => e => {
            if (currentIndex === 0) return
            animateSlide(currentIndex - 1)
            onIndexChange({ currentIndex: currentIndex - 1, maxIndex })
            onPrev({ currentIndex: currentIndex - 1, maxIndex })
            return { currentIndex: currentIndex - 1 }
        },
        handleRightArrClick: ({ currentIndex, maxIndex, onIndexChange, onNext }) => e => {
            if (currentIndex === maxIndex) return
            animateSlide(currentIndex + 1)
            onIndexChange({ currentIndex: currentIndex + 1, maxIndex })
            onNext({ currentIndex: currentIndex + 1, maxIndex })
            return { currentIndex: currentIndex + 1 }
        },
        handleContentClick: ({ onClick, currentIndex, mediaList }) => e => {
            onClick({ e, currentIndex, content: mediaList[currentIndex] })
        },
        resizeHandler: ({ currentIndex }) => e => {
            TweenLite.to(carousel, 0.0001, {
                scrollLeft: carousel.getBoundingClientRect().width * currentIndex,
            })
        },
    }
)

const touchHandlers = withStateHandlers(
    ({ handleLeftArrClick, handleRightArrClick }) => ({
        lastTouch: undefined,
        handleLeftArrClick,
        handleRightArrClick,
    }),
    {
        handleTouchEnd: ({ lastTouch, handleLeftArrClick, handleRightArrClick }) => e => {
            const delta = e.changedTouches[0].clientX - lastTouch
            if (Math.abs(delta) > 75) {
                delta > 0 ? handleLeftArrClick() : handleRightArrClick()
            }
        },
        handleTouchStart: () => e => {
            return { lastTouch: e.changedTouches[0].clientX }
        },
    }
)

const animateSlide = index => {
    TweenLite.to(carousel, 0.3, {
        ease: Power2.easeInOut,
        scrollLeft: carousel.getBoundingClientRect().width * index,
    })
}

class MediaCarousel extends PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            expandedReadMore: false,
        }
    }

    componentDidMount() {
        window.addEventListener('resize', this.props.resizeHandler)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.props.resizeHandler)
    }

    handleExpandReadMore = () => {
        const { expandedReadMore } = this.state
        this.setState({ expandedReadMore: !expandedReadMore })
    }

    renderMobileLowerContent = () => {
        const {
            classes,
            currentIndex,
            mediaList,
            lowerContentClassName,
            textContentClassName,
            titleClassName,
            descriptionClassName,
            isLandscape,
        } = this.props
        const { expandedReadMore } = this.state

        return (
            <div>
                <div
                    className={classNames(
                        isLandscape ? classes.landscapeLowerContent : classes.lowerContent,
                        lowerContentClassName
                    )}
                    height={expandedReadMore ? '100vh' : ''}
                >
                    {expandedReadMore && isLandscape && <div className={classes.mobileLongDescriptionBackground} />}
                    <div
                        className={classNames(
                            !expandedReadMore && isLandscape
                                ? classes.mobileShortTextContent
                                : classes.mobileLongTextContent,
                            textContentClassName
                        )}
                    >
                        <div
                            className={classNames(classes.title, titleClassName)}
                            dangerouslySetInnerHTML={{ __html: mediaList[currentIndex].title }}
                        />
                        {expandedReadMore || !isLandscape ? (
                            <div className={classNames(classes.mobileLongDescription, descriptionClassName)}>
                                <div dangerouslySetInnerHTML={{ __html: mediaList[currentIndex].description }} />
                            </div>
                        ) : (
                            <Clamp clamp={1}>
                                <div
                                    className={classNames(classes.mobileShortDescription, descriptionClassName)}
                                    dangerouslySetInnerHTML={{ __html: mediaList[currentIndex].description }}
                                />
                            </Clamp>
                        )}
                    </div>
                </div>
                {!expandedReadMore && isLandscape && <div className={classes.mobileShortDescriptionBackground} />}
                {isLandscape && mediaList[currentIndex].description.length > 0 && (
                    <div className={classes.readMoreContainer}>
                        <Button
                            className={classes.readMoreButton}
                            labelStyle={inlineStyle.readMoreButton}
                            onClick={() => this.handleExpandReadMore()}
                        >
                            {expandedReadMore ? (
                                <span className={classes.expandText}>LESS</span>
                            ) : (
                                <span className={classes.expandText}>MORE</span>
                            )}
                            {expandedReadMore ? (
                                <Icon className={classes.expandIcons}>expand_less</Icon>
                            ) : (
                                <Icon className={classes.expandIcons}>expand_more</Icon>
                            )}
                        </Button>
                    </div>
                )}
            </div>
        )
    }

    renderDesktopLowerContent = () => {
        const {
            classes,
            currentIndex,
            mediaList,
            lowerContentClassName,
            textContentClassName,
            titleClassName,
            descriptionClassName,
        } = this.props

        return (
            <div className={classNames(classes.lowerContent, lowerContentClassName)}>
                <div className={classNames(classes.desktopTextContent, textContentClassName)}>
                    <div
                        className={classNames(classes.title, titleClassName)}
                        dangerouslySetInnerHTML={{ __html: mediaList[currentIndex].title }}
                    />
                    <div
                        className={classNames(classes.desktopDescription, descriptionClassName)}
                        dangerouslySetInnerHTML={{ __html: mediaList[currentIndex].description }}
                    />
                </div>
            </div>
        )
    }

    render() {
        const {
            classes,
            render,
            currentIndex,
            maxIndex,
            handleLeftArrClick,
            handleRightArrClick,
            mediaList,
            handleContentClick,
            showIndicators,
            handleTouchStart,
            handleTouchEnd,
            containerClassName,
            slideClassName,
            isMobile,
        } = this.props

        return (
            <div className={classNames(classes.container, containerClassName)}>
                <div className={classes.innerContent}>
                    <div className={classNames(classes.arrow, classes.leftArrow)} onClick={handleLeftArrClick}>
                        <div className={classNames(classes.arrowIcon, 'material-icons')}>chevron_left</div>
                    </div>
                    <div
                        className={classNames('carousel', classes.carouselWrapper)}
                        ref={ref => (carousel = ref)}
                        onClick={handleContentClick}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div className={classes.carousel}>
                            {mediaList.map((item, i) => (
                                <Slide
                                    className={
                                        typeof slideClassName === 'function' ? slideClassName(item, i) : slideClassName
                                    }
                                    data={mediaList}
                                    key={i}
                                >
                                    {render(item)}
                                </Slide>
                            ))}
                        </div>
                    </div>
                    <div className={classNames(classes.arrow, classes.rightArrow)} onClick={handleRightArrClick}>
                        <div className={classNames(classes.arrowIcon, 'material-icons')}>chevron_right</div>
                    </div>
                    <Indicators {...{ currentIndex, maxIndex, showIndicators }} />
                </div>
                {isMobile ? this.renderMobileLowerContent() : this.renderDesktopLowerContent()}
            </div>
        )
    }
}

MediaCarousel.propTypes = {
    showIndicators: PropTypes.bool,
    showArrows: PropTypes.bool,
    mediaList: PropTypes.array,
    showLowerContent: PropTypes.bool,
    onClick: PropTypes.func,
    isFullWidthTitleDescription: PropTypes.bool,
}

export default compose(
    state,
    touchHandlers,
    props,
    connect(mapSelectorsToProps(['isLandscape']), null),
    injectJss(style)
)(MediaCarousel)
